/* Resolve theme tokens to concrete colors for the two places that cannot use a
 * CSS variable directly: a 2D canvas context and three.js materials.
 *
 * Everything drawn by CSS — including Recharts props, which land on SVG
 * presentation attributes and are parsed as CSS values — should just use
 * `var(--acc)` and skip this module. There is deliberately no second copy of
 * the palette in JavaScript; theme.css stays the only place colors are written.
 *
 * Reads are cached per theme, because the supply-chain map calls this inside a
 * requestAnimationFrame loop and getComputedStyle forces a style recalc. */

type Cache = { theme: string; values: Map<string, string> };
let cache: Cache | null = null;

function activeTheme(): string {
  // Light is the product default (index.html stamps it), so an unstamped
  // document keys the cache the same way the CSS resolves it.
  return document.documentElement.getAttribute('data-theme') || 'light';
}

/** The computed value of a custom property, e.g. token(el, 'acc') → 'rgb(34 211 238)'. */
function token(el: Element, name: string): string {
  const theme = activeTheme();
  if (!cache || cache.theme !== theme) cache = { theme, values: new Map() };
  const hit = cache.values.get(name);
  if (hit !== undefined) return hit;
  const value = getComputedStyle(el).getPropertyValue(`--${name}`).trim();
  cache.values.set(name, value);
  return value;
}

/** A solid theme color: cssColor(canvas, 'acc') → 'rgb(34 211 238)'. */
export function cssColor(el: Element, name: string): string {
  return token(el, name) || '#000';
}

/** A theme color at an alpha: cssAlpha(canvas, 'acc', .4) → 'rgb(34 211 238 / 0.4)'.
 *  Takes the channel triplet (--c-acc), so it works for any accent token. */
export function cssAlpha(el: Element, name: string, alpha: number): string {
  const channels = token(el, `c-${name}`);
  return channels ? `rgb(${channels} / ${alpha})` : cssColor(el, name);
}

/** A theme color as a three.js-friendly 0xRRGGBB number. */
export function cssColorHex(el: Element, name: string, fallback = 0x000000): number {
  const channels = token(el, `c-${name}`);
  if (!channels) return fallback;
  const parts = channels.split(/[\s,]+/).map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return fallback;
  const [r, g, b] = parts as [number, number, number];
  return ((r & 255) << 16) | ((g & 255) << 8) | (b & 255);
}

/** Drop the cache. Only needed if theme.css itself is swapped at runtime. */
export function resetCssColorCache(): void {
  cache = null;
}
